/**
 * PlaybackVideo - shared HTML5 video player for interview / mock replays.
 *
 * Two modes:
 *
 *  1. Single-source (only `src`, or `srcScreen` failed to load). Plain
 *     <video controls> + our speed pills + the WebM duration workaround.
 *
 *  2. Dual-source (`src` + `srcScreen`, both load fine). Two <video>
 *     elements share a stable layout: whichever feed is in focus sits
 *     full-bleed with native controls; the other floats as a muted
 *     picture-in-picture inset in the bottom-left. A swap button on
 *     top of the PiP flips the focus -- impossible with the previous
 *     baked-in canvas composite.
 *
 *     Audio always plays from the cam track (it carries the candidate
 *     mic + AI mix). The screen track is muted and decorative.
 *
 *     The two recordings come from separate MediaRecorders that started
 *     within ~50ms of each other, so they need light-touch sync. We
 *     ONLY install the sync listeners once BOTH videos have reported a
 *     finite duration. Until then their <video> elements act as fully
 *     independent players -- this avoids the duration-fix dance from
 *     ping-ponging seek events between the two streams.
 *
 *  WebM "Infinity duration" workaround
 *  -----------------------------------
 *  Recordings are written as Azure Append Blobs from MediaRecorder
 *  chunks. The result is a valid playable WebM, but its EBML header
 *  has no SegmentDuration entry -- so `video.duration === Infinity` and
 *  the native scrubber goes nuts. We seek past the end of the file
 *  once metadata loads; the browser then scans the actual last cluster
 *  and `duration` becomes correct. We snap back to 0 and from then on
 *  the scrubber tracks position correctly.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeftRight, Loader2, Maximize2, Play } from 'lucide-react';

const PLAYBACK_SPEEDS: number[] = [0.5, 1, 1.5, 2];
// A real recording can't possibly seek past this far -- anything
// beyond it is the duration-fix seek to 1e10 looking for the end of
// the file. Used to filter out those "seek past end" pulses so we
// don't propagate them into the sister stream.
const DURATION_FIX_THRESHOLD = 1_000_000;

// "Close enough to the end of the recording that pressing play again
// should restart from 0 instead of re-firing 'ended' immediately."
// Browsers don't auto-rewind a `<video>` that has reached `duration`
// when the user clicks play -- we have to do it manually.
const END_EPSILON = 0.25;

const isAtEnd = (v: HTMLVideoElement): boolean =>
  Number.isFinite(v.duration) && v.duration > 0 && v.currentTime >= v.duration - END_EPSILON;

const rewindIfStuck = (v: HTMLVideoElement): void => {
  // Snap back to 0 from either of the two stuck states the WebM
  // duration dance + native controls can leave us in:
  //   - currentTime > 1e6 (the 1e10 duration-fix probe leaked through)
  //   - currentTime ~= duration (we're sitting on 'ended')
  if (v.currentTime > DURATION_FIX_THRESHOLD || isAtEnd(v)) {
    try { v.currentTime = 0; } catch { /* ignore */ }
  }
};

interface Props {
  /** Cam track (webcam + audio mix). Required. */
  src: string;
  /**
   * Optional screen-capture track. When provided AND it loads
   * successfully, the player renders in dual-source mode. If the
   * screen track 404s or errors, we silently fall back to single mode.
   */
  srcScreen?: string | null;
  fallback: string;
}

// WebM duration fix.
//
// Recordings written from MediaRecorder chunks land in Azure as Append
// Blobs and the EBML header is missing the SegmentDuration entry, so
// `video.duration` reads as Infinity on first load. The classic fix
// is to seek past the end (currentTime = 1e10), wait for the browser
// to scan to the actual end and report the real duration, then snap
// back to 0.
//
// Subtle bug we hit on the dual player: after that snap-back some
// Chromium builds leave the video in a state where pressing play
// does nothing -- the readyState briefly dropped to HAVE_NOTHING
// during the long seek and never reached HAVE_FUTURE_DATA again
// without an explicit reload of the buffered range at 0. We work
// around that by triggering a tiny "wake the buffer up" seek (0 ->
// 0.001 -> 0) once duration is known. It's cheap, lossless, and
// reliably lands the video at 0 with data ready to play.
//
// Idempotent: tearing down before it finishes is safe -- nothing is
// modified that can't be re-run on the next mount.
//
// The optional `onResolved` callback fires once `video.duration` has
// settled to a finite value AND we've snapped currentTime back to 0.
// Callers gate the play button on this so users don't kick off
// playback while the scrubber still shows nonsense (the 1e10 seek
// briefly leaks into `currentTime` and the native progress bar
// renders at ~100% as a side effect).
function attachDurationFix(v: HTMLVideoElement, onResolved?: () => void): () => void {
  let resolved = false;

  const markResolved = () => {
    if (resolved) return;
    resolved = true;
    onResolved?.();
  };

  const wakeBuffer = () => {
    // Two tiny seeks bracketing 0 force the browser to refresh its
    // notion of the buffered range without changing the visible
    // start position.
    try {
      v.currentTime = 0.001;
      queueMicrotask(() => {
        try { v.currentTime = 0; } catch { /* ignore */ }
      });
    } catch {
      // currentTime might be unsettable yet; give up silently, native
      // controls will still let the user scrub manually.
    }
  };

  const tryFix = () => {
    if (resolved) return;
    if (Number.isFinite(v.duration) && v.duration > 0) {
      wakeBuffer();
      markResolved();
      return;
    }
    const onDurationChange = () => {
      if (Number.isFinite(v.duration) && v.duration > 0) {
        v.removeEventListener('durationchange', onDurationChange);
        try {
          v.currentTime = 0;
          wakeBuffer();
        } catch {
          queueMicrotask(() => {
            try { v.currentTime = 0; wakeBuffer(); } catch { /* give up */ }
          });
        }
        markResolved();
      }
    };
    v.addEventListener('durationchange', onDurationChange);
    try { v.currentTime = 1e10; } catch { /* ignore */ }
  };

  v.addEventListener('loadedmetadata', tryFix);
  v.addEventListener('durationchange', tryFix);
  if (v.readyState >= 1) tryFix();

  return () => {
    v.removeEventListener('loadedmetadata', tryFix);
    v.removeEventListener('durationchange', tryFix);
  };
}

export const PlaybackVideo: React.FC<Props> = ({ src, srcScreen, fallback }) => {
  // When srcScreen is provided we still need to verify it actually
  // loads. A common case in dual-stream: a session where the cam blob
  // exists but the screen blob is empty / 404 (network issue mid-call,
  // or the user revoked screen-share before any chunk landed). The
  // backend returns a SAS URL even for those blobs, so we can't know
  // ahead of time. The dual player listens for `error` on the screen
  // element and falls back to single mode if loading fails.
  const [screenFailed, setScreenFailed] = useState(false);
  useEffect(() => {
    setScreenFailed(false); // reset when URLs change
  }, [src, srcScreen]);

  // Stable identity so DualPlaybackVideo's useEffects don't re-fire on
  // every parent re-render -- otherwise the inline arrow caused the
  // readiness effect to reset setScreenReady(false) on each render and
  // the "Preparing playback..." overlay never resolved.
  const handleScreenError = useCallback(() => setScreenFailed(true), []);

  if (srcScreen && !screenFailed) {
    return (
      <DualPlaybackVideo
        src={src}
        srcScreen={srcScreen}
        fallback={fallback}
        onScreenError={handleScreenError}
      />
    );
  }
  return <SinglePlaybackVideo src={src} fallback={fallback} />;
};

// ============================================================
// Single-source mode (legacy / no-screen recordings)
// ============================================================

const SinglePlaybackVideo: React.FC<{ src: string; fallback: string }> = ({ src, fallback }) => {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [rate, setRate] = useState<number>(1);
  // Track whether the user has explicitly kicked off playback at least
  // once. Until then we render a big custom Play button on top of the
  // <video>. Reason: relying on Chromium's native controls during the
  // WebM duration-fix dance left users on the recorded screen unable
  // to start playback in some cases ("Play passiert nichts"). A custom
  // button that calls .play() directly is bulletproof; after the first
  // start the native controls take over for pause / scrub / etc.
  const [started, setStarted] = useState(false);
  // True once attachDurationFix has settled the WebM duration. Before
  // this fires the native scrubber renders nonsense values (the 1e10
  // seek leaks into currentTime and the scrubber appears ~100% full)
  // and seeking does not work. We disable the Play overlay until then.
  const [ready, setReady] = useState(false);

  const apply = (r: number) => {
    setRate(r);
    if (ref.current) ref.current.playbackRate = r;
  };

  // Re-apply the desired rate whenever the <video> element's own
  // ratechange fires with a different value (e.g. the native
  // controls speed menu was used, or the duration-fix's seek
  // momentarily snapped it back to 1). Keeps our SpeedBar as the
  // source of truth.
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    const enforce = () => {
      if (Math.abs(v.playbackRate - rate) > 0.01) {
        v.playbackRate = rate;
      }
    };
    enforce();
    v.addEventListener('ratechange', enforce);
    v.addEventListener('playing', enforce);
    return () => {
      v.removeEventListener('ratechange', enforce);
      v.removeEventListener('playing', enforce);
    };
  }, [rate]);

  const startPlay = useCallback(() => {
    const v = ref.current;
    if (!v) return;
    // Snap back to 0 if we're stuck at the duration-fix probe OR
    // sitting on 'ended' from a previous play-through.
    rewindIfStuck(v);
    void v.play()
      .then(() => setStarted(true))
      .catch((e) => {
        console.warn('PlaybackVideo: play() rejected', e);
      });
  }, []);

  useEffect(() => {
    setStarted(false);
    setReady(false);
    const v = ref.current;
    if (!v) return undefined;
    const teardown = attachDurationFix(v, () => setReady(true));
    // Failsafe: if duration never resolves (broken blob, browser quirk)
    // unlock the Play button after 8s so users aren't stuck on a
    // permanent spinner. The scrubber may still misbehave but at least
    // they can start playback.
    const watchdog = window.setTimeout(() => setReady(true), 8000);
    // When playback reaches the end, drop `started` so our big Play
    // overlay reappears. Browsers don't auto-rewind on the next click
    // of native controls and it left users staring at a frozen frame
    // wondering why play does nothing. Now: ended → overlay → click →
    // startPlay → rewindIfStuck → play from 0. Replay just works.
    const onEnded = () => setStarted(false);
    v.addEventListener('ended', onEnded);
    return () => {
      teardown();
      window.clearTimeout(watchdog);
      v.removeEventListener('ended', onEnded);
    };
  }, [src]);

  return (
    <>
      <SpeedBar rate={rate} onChange={apply} />
      <div className="relative w-full bg-black">
        <video
          ref={ref}
          controls={started}
          playsInline
          preload="auto"
          controlsList="nodownload"
          src={src}
          onPlay={() => setStarted(true)}
          className="w-full h-auto max-h-[70vh] bg-black block"
        >
          {fallback}
        </video>
        {!started && <PlayOverlay onClick={startPlay} ready={ready} />}
      </div>
    </>
  );
};

// ============================================================
// Dual-source mode (cam + screen)
// ============================================================

type FocusKind = 'cam' | 'screen';

const DualPlaybackVideo: React.FC<{
  src: string;
  srcScreen: string;
  fallback: string;
  /** Called if the screen <video> emits an error before metadata. */
  onScreenError: () => void;
}> = ({ src, srcScreen, fallback, onScreenError }) => {
  const camRef = useRef<HTMLVideoElement | null>(null);
  const screenRef = useRef<HTMLVideoElement | null>(null);
  // Re-entrancy guard: when we programmatically apply a play/pause/seek
  // on the slave, the resulting event would bounce back and try to
  // re-apply on the master. The flag short-circuits that bounce.
  const syncingRef = useRef<boolean>(false);
  const [rate, setRate] = useState<number>(1);
  // We used to gate the sync listeners + controls behind "both videos
  // report a finite duration". In practice that introduced more bugs
  // than it solved (a parent re-render with a fresh SAS URL flipped
  // readiness back to false and the "Preparing playback..." overlay
  // re-appeared on every scorecard-poll cycle). The simpler design
  // installs sync as soon as both <video> elements exist; the
  // duration-fix runs silently. The mirror() routine itself filters
  // out the 1e10 seek so it doesn't propagate across the two streams.
  const [syncReady, setSyncReady] = useState(false);
  const [started, setStarted] = useState(false);
  // Per-stream readiness flags from the duration-fix resolver. The
  // Play overlay stays disabled until BOTH are true so users don't
  // see the broken scrubber state (Infinity duration → 1e10 leaks
  // into the progress bar, no seek possible).
  const [camReady, setCamReady] = useState(false);
  const [screenReady, setScreenReady] = useState(false);
  const bothReady = camReady && screenReady;
  // Screen is the more interesting feed in 95% of interviews (IDE,
  // whiteboard, problem statement) so we default to screen-big.
  const [focus, setFocus] = useState<FocusKind>('screen');
  // PiP size presets. Native scrubber lives at the bottom of the main
  // video, so we anchor the PiP to the TOP-RIGHT corner instead. Three
  // sizes is enough -- more would feel fiddly and "drag-to-resize" is
  // overkill for a review tool.
  const PIP_SIZES = ['sm', 'md', 'lg'] as const;
  type PipSize = (typeof PIP_SIZES)[number];
  const [pipSize, setPipSize] = useState<PipSize>('md');
  const cyclePipSize = useCallback(() => {
    setPipSize((s) => {
      const i = PIP_SIZES.indexOf(s);
      return PIP_SIZES[(i + 1) % PIP_SIZES.length];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = (r: number) => {
    setRate(r);
    syncingRef.current = true;
    if (camRef.current) camRef.current.playbackRate = r;
    if (screenRef.current) screenRef.current.playbackRate = r;
    queueMicrotask(() => { syncingRef.current = false; });
  };

  // Same enforcement effect as in single mode: re-pin the picked
  // rate if anything snaps either video back to 1x (native controls,
  // duration-fix seek, etc.). Keeps SpeedBar authoritative on both
  // tracks.
  useEffect(() => {
    const cam = camRef.current;
    const scr = screenRef.current;
    const enforce = () => {
      [cam, scr].forEach((v) => {
        if (v && Math.abs(v.playbackRate - rate) > 0.01) {
          v.playbackRate = rate;
        }
      });
    };
    enforce();
    cam?.addEventListener('ratechange', enforce);
    cam?.addEventListener('playing', enforce);
    scr?.addEventListener('ratechange', enforce);
    scr?.addEventListener('playing', enforce);
    return () => {
      cam?.removeEventListener('ratechange', enforce);
      cam?.removeEventListener('playing', enforce);
      scr?.removeEventListener('ratechange', enforce);
      scr?.removeEventListener('playing', enforce);
    };
  }, [rate]);

  // Custom kickoff: explicitly play() both videos. Same rationale as
  // Single mode -- relying on the native controls during the WebM
  // duration-fix window left users unable to start playback in some
  // Chromium builds. Snap currentTime back to 0 on both if the fix
  // is still in flight.
  const startPlay = useCallback(() => {
    const cam = camRef.current;
    const scr = screenRef.current;
    [cam, scr].forEach((v) => {
      if (!v) return;
      // Same rewind logic as single mode -- snaps back to 0 from
      // both the duration-fix probe and the 'ended' state so a
      // post-watch click restarts cleanly.
      rewindIfStuck(v);
    });
    syncingRef.current = true;
    const ps: Promise<unknown>[] = [];
    if (cam) ps.push(cam.play().catch((e) => console.warn('cam.play() rejected', e)));
    if (scr) ps.push(scr.play().catch((e) => console.warn('scr.play() rejected', e)));
    Promise.allSettled(ps).then(() => {
      syncingRef.current = false;
      setStarted(true);
    });
  }, []);

  // Cam side: attach the duration fix and flip camReady when done.
  useEffect(() => {
    setCamReady(false);
    const v = camRef.current;
    if (!v) return undefined;
    const teardown = attachDurationFix(v, () => setCamReady(true));
    // Failsafe -- see SinglePlaybackVideo for rationale.
    const watchdog = window.setTimeout(() => setCamReady(true), 8000);
    return () => {
      teardown();
      window.clearTimeout(watchdog);
    };
  }, [src]);

  // Screen side: attach the duration fix and an error fallback. If the
  // screen blob errors out we tell the parent to flip back to single mode.
  useEffect(() => {
    setScreenReady(false);
    const v = screenRef.current;
    if (!v) return undefined;
    const teardownFix = attachDurationFix(v, () => setScreenReady(true));
    const watchdog = window.setTimeout(() => setScreenReady(true), 8000);
    const onError = () => onScreenError();
    v.addEventListener('error', onError);
    return () => {
      teardownFix();
      window.clearTimeout(watchdog);
      v.removeEventListener('error', onError);
    };
  }, [srcScreen, onScreenError]);

  // Mount-once flag so sync gets installed exactly once after both
  // <video> refs exist.
  useEffect(() => {
    if (camRef.current && screenRef.current) setSyncReady(true);
  }, []);

  // Reset the "started" gate whenever the source URLs change (e.g. user
  // navigates to a different session, or the parent re-renders with new
  // SAS URLs). Without this, sticky `started=true` would hide the Play
  // overlay before the new videos are ready.
  useEffect(() => {
    setStarted(false);
  }, [src, srcScreen]);

  // When EITHER stream reaches the end, drop `started` so our big Play
  // overlay reappears. Same rationale as in single mode: browsers
  // don't auto-rewind from `currentTime === duration` on a fresh play
  // click via native controls, so we take over the replay UX.
  useEffect(() => {
    const cam = camRef.current;
    const scr = screenRef.current;
    if (!cam || !scr) return undefined;
    const onEnded = () => setStarted(false);
    cam.addEventListener('ended', onEnded);
    scr.addEventListener('ended', onEnded);
    return () => {
      cam.removeEventListener('ended', onEnded);
      scr.removeEventListener('ended', onEnded);
    };
  }, []);

  // Bi-directional sync. The mirror() routine itself filters out the
  // duration-fix seek-past-end so we don't need a separate readiness
  // gate here -- a 1e10 currentTime is simply ignored.
  useEffect(() => {
    if (!syncReady) return undefined;
    const cam = camRef.current;
    const scr = screenRef.current;
    if (!cam || !scr) return undefined;

    // Snap the slave to the master's currentTime when drift exceeds
    // this threshold (seconds). 250ms is well below what a viewer
    // would notice but above the jitter between two <video> tags.
    const DRIFT_LIMIT = 0.25;

    // Position-equality epsilon: if master and slave are already
    // within this many seconds of each other, skip the redundant
    // re-seek. Critical for breaking the master ↔ slave seeked
    // feedback loop -- when we set slave.currentTime to master's,
    // slave fires its own 'seeked' event which would otherwise come
    // back here and re-set master.currentTime to slave's (slightly
    // different due to keyframe snapping), and we'd ping-pong forever
    // around the user's seek target. queueMicrotask-based syncingRef
    // alone wasn't enough because slave's 'seeked' is a macrotask and
    // arrives AFTER the microtask has already cleared the flag.
    const POS_EPSILON = 0.12;

    const mirror = (master: HTMLVideoElement, slave: HTMLVideoElement, event: string) => {
      if (syncingRef.current) return;
      // Defensive: ignore the duration-fix's "seek past the end" if
      // it ever sneaks through after bothReady is true (some browsers
      // re-fire seeking on src changes).
      if (master.currentTime > DURATION_FIX_THRESHOLD) return;
      syncingRef.current = true;
      try {
        switch (event) {
          case 'play':
            void slave.play().catch(() => undefined);
            break;
          case 'pause':
            // Don't propagate a pause that was just emitted by the
            // browser to scrub: when the user drags the native
            // scrubber the browser fires pause → seeking → seeked →
            // play. If we forward the pause and master then resumes,
            // slave races to catch up via the play-event branch,
            // which sometimes loses (Chromium decides slave is still
            // seeking and skips the play()). Result for the user:
            // "I scrubbed and the small video stopped". By skipping
            // the pause forward we let the slave keep playing through
            // a scrub; the seek branch realigns its currentTime.
            if (master.seeking) break;
            slave.pause();
            break;
          case 'seeked': {
            // Only mirror if the gap actually warrants a re-seek. If
            // master and slave are already aligned this event is the
            // tail of an echo from a previous slave.currentTime = ...
            // we did ourselves -- propagating it would seed a feedback
            // loop and we'd see the playhead twitch around the same
            // ~200ms region forever ("spielt selbe Stelle ab"). Skip
            // when we're close enough.
            if (Math.abs(master.currentTime - slave.currentTime) < POS_EPSILON) {
              break;
            }
            try { slave.currentTime = master.currentTime; } catch { /* ignore */ }
            break;
          }
          case 'ratechange':
            // ONLY mirror the rate. The previous version also
            // re-seeked slave to master.currentTime, which triggered
            // a stall just as the new rate was about to take effect
            // and made our SpeedBar feel like it did nothing.
            slave.playbackRate = master.playbackRate;
            break;
          case 'timeupdate': {
            const drift = Math.abs(slave.currentTime - master.currentTime);
            if (drift > DRIFT_LIMIT) {
              try { slave.currentTime = master.currentTime; } catch { /* ignore */ }
            }
            break;
          }
          default:
            break;
        }
      } finally {
        queueMicrotask(() => { syncingRef.current = false; });
      }
    };

    // 'seeking' is intentionally absent: it fires repeatedly while the
    // user drags the scrubber and would put the slave through a
    // re-seek storm. We mirror only the final 'seeked' position.
    // 'playing' is intentionally absent: it fires on every buffer
    // resume / stall recovery, which during a seek causes both videos
    // to fire it back-to-back and trampoline play() calls between
    // each other. The 'play' event alone is enough to capture user-
    // initiated playback start; re-buffering doesn't need a sync.
    const EVENTS = ['play', 'pause', 'seeked', 'ratechange', 'timeupdate'];
    const camHandlers = EVENTS.map((ev) => {
      const h = () => mirror(cam, scr, ev);
      cam.addEventListener(ev, h);
      return { ev, h };
    });
    const scrHandlers = EVENTS.map((ev) => {
      const h = () => mirror(scr, cam, ev);
      scr.addEventListener(ev, h);
      return { ev, h };
    });
    return () => {
      camHandlers.forEach(({ ev, h }) => cam.removeEventListener(ev, h));
      scrHandlers.forEach(({ ev, h }) => scr.removeEventListener(ev, h));
    };
  }, [syncReady]);

  const swapFocus = useCallback(() => {
    setFocus((prev) => (prev === 'cam' ? 'screen' : 'cam'));
  }, []);

  const isCamMain = focus === 'cam';

  // Layout: each video has a STABLE ref so sync listeners stay attached
  // across focus changes. The "big vs PiP" swap is CSS-only -- the in-
  // focus element gets sized as the container, the other floats in the
  // corner. Audio always comes from cam (cam is never muted, screen
  // always is) so the viewer keeps hearing the conversation when they
  // put the screen capture in focus.
  const mainClass = 'w-full h-auto max-h-[70vh] bg-black block';
  // Width per preset. Heights follow the 16:9 aspect-ratio class.
  const PIP_WIDTH: Record<PipSize, string> = {
    sm: 'w-[16%] max-w-[160px]',
    md: 'w-[24%] max-w-[240px]',
    lg: 'w-[34%] max-w-[340px]',
  };
  const pipClass = [
    'absolute top-3 right-3',
    PIP_WIDTH[pipSize],
    'aspect-video object-cover rounded-lg overflow-hidden border-2 border-white/70 shadow-[0_8px_24px_rgba(0,0,0,0.55)] bg-black z-10 pointer-events-none',
  ].join(' ');

  return (
    <>
      <SpeedBar rate={rate} onChange={apply} />
      <div className="relative w-full bg-black">
        {/*
          Native <video controls> are gated on `bothReady` (= the WebM
          duration-fix has settled on BOTH streams). Before that, the
          browser renders garbage time / duration ("Infinity", 1e10, …)
          AND silently swallows user seeks because seekable ranges
          haven't been computed yet. Showing controls only after both
          videos are ready means: clean time display + working scrub.
        */}
        <video
          ref={camRef}
          controls={isCamMain && bothReady}
          playsInline
          preload="auto"
          controlsList="nodownload"
          src={src}
          muted={false}
          className={isCamMain ? mainClass : pipClass}
        >
          {fallback}
        </video>
        <video
          ref={screenRef}
          controls={!isCamMain && bothReady}
          playsInline
          preload="auto"
          controlsList="nodownload"
          src={srcScreen}
          muted
          className={!isCamMain ? mainClass : pipClass}
        >
          {fallback}
        </video>

        {/* Custom kickoff overlay. Disappears after the first play()
            so the native controls can take over for pause / scrub /
            volume. See `startPlay`. Disabled until BOTH streams have
            settled their WebM duration -- otherwise the scrubber +
            time display would be broken at start. */}
        {!started && <PlayOverlay onClick={startPlay} ready={bothReady} />}

        {/* Swap button + PiP size cycle live in the top-right corner so
            they stay clear of the native controls at the bottom. */}
        {syncReady && (
          <div
            className={[
              'absolute top-3 right-3 z-20',
              PIP_WIDTH[pipSize],
              'aspect-video group',
            ].join(' ')}
          >
            {/* Big click target = swap focus. Empty button covering the
                whole PiP rectangle so a click anywhere on the inset
                feels like "bring this to the front". */}
            <button
              type="button"
              onClick={swapFocus}
              aria-label={isCamMain ? 'Show screen' : 'Show webcam'}
              className="absolute inset-0 rounded-lg flex items-center justify-center bg-black/0 hover:bg-black/40 transition-colors"
            >
              <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 text-slate-900 text-[11px] font-semibold shadow">
                <ArrowLeftRight className="w-3 h-3" />
                {isCamMain ? 'Show screen' : 'Show webcam'}
              </span>
            </button>
            {/* Size cycle lives on top of the swap target, in the inset's
                top-left corner. stopPropagation so a click here doesn't
                also swap. Tiny + always visible on hover. */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); cyclePipSize(); }}
              aria-label="Resize inset"
              title={`Inset size: ${pipSize === 'sm' ? 'S' : pipSize === 'md' ? 'M' : 'L'} (click to cycle)`}
              className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-black/55 hover:bg-black/80 text-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// ============================================================
// Shared bits
// ============================================================

// Big custom Play overlay used before the user kicks off playback.
// Renders as `absolute inset-0` so a click ANYWHERE on the video
// surface counts -- removing the "I clicked but nothing happened"
// problem from before. After the first play() the parent sets
// `started=true` and the overlay unmounts; native controls take
// over from there.
//
// `ready=false` swaps the icon for a spinner and disables the click
// so the user can't kick off playback while the WebM duration fix is
// still in flight (during that window the scrubber + time display
// are nonsense).
const PlayOverlay: React.FC<{ onClick: () => void; ready: boolean }> = ({ onClick, ready }) => (
  <button
    type="button"
    onClick={ready ? onClick : undefined}
    aria-label={ready ? 'Play recording' : 'Loading recording'}
    disabled={!ready}
    className={[
      'absolute inset-0 z-30 flex items-center justify-center transition-colors group',
      ready
        ? 'bg-black/30 hover:bg-black/40 cursor-pointer'
        : 'bg-black/50 cursor-wait',
    ].join(' ')}
  >
    <span
      className={[
        'inline-flex items-center justify-center w-16 h-16 rounded-full',
        'shadow-[0_8px_24px_rgba(0,0,0,0.55)] transition-transform',
        ready
          ? 'bg-white/95 text-slate-900 group-hover:scale-110'
          : 'bg-white/70 text-slate-700',
      ].join(' ')}
    >
      {ready ? (
        <Play className="w-7 h-7 ml-1" fill="currentColor" />
      ) : (
        <Loader2 className="w-7 h-7 animate-spin" />
      )}
    </span>
  </button>
);

const SpeedBar: React.FC<{ rate: number; onChange: (r: number) => void }> = ({ rate, onChange }) => (
  <div className="flex items-center justify-end gap-1 px-3 py-2 bg-slate-900/60 border-b border-slate-800/60">
    <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mr-1">
      Speed
    </span>
    {PLAYBACK_SPEEDS.map((s) => (
      <button
        key={s}
        type="button"
        onClick={() => onChange(s)}
        className={[
          'px-2 py-0.5 rounded-full text-[11px] font-semibold transition-colors',
          rate === s
            ? 'bg-white text-slate-900'
            : 'text-white/60 hover:text-white hover:bg-white/10',
        ].join(' ')}
      >
        {s}x
      </button>
    ))}
  </div>
);

export default PlaybackVideo;
